import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { GoogleMap, useJsApiLoader, Marker, Polyline, OverlayView, type Libraries } from '@react-google-maps/api'
import {
  Search, X, RefreshCw, Radio, MapPin, Gauge, Clock, Route as RouteIcon,
  User, GraduationCap, ExternalLink, Navigation, TimerReset, Calendar,
  AlertTriangle, ChevronLeft, ChevronRight, CalendarClock, ArrowLeft,
  Layers, Crosshair, Activity, Satellite, SlidersHorizontal,
} from 'lucide-react'
import { Spinner } from '@/shared/ui/Spinner'
import { GOOGLE_MAPS_API_KEY } from '@/shared/constants'
import { cn } from '@/shared/utils'
import { bookingService } from '@/api/services/bookingService'
import { gpsService, type ActiveSession, type RoutePoint, type SessionDetail } from '@/api/services/gpsService'

// Same loader id/libraries as the dashboard heat map so the Maps script loads once.
const LIBRARIES: Libraries = ['places']
const DEFAULT_CENTER = { lat: 24.4539, lng: 54.3773 }   // Abu Dhabi
const LIST_REFRESH_MS  = 15_000
const TRACK_REFRESH_MS = 8_000
const BOOKING_PAGE_SIZE = 10
// A live session whose newest GPS point is older than this is treated as stale —
// the device has stopped reporting even though the session is still open.
const STALE_GPS_SEC    = 120
// Two consecutive recorded points further apart than this in time mean the
// signal dropped in between — that gap must not be drawn as a straight line of
// travel (the trainer didn't teleport), so the route splits there instead.
const ROUTE_GAP_SEC    = 90

// Muted base map: the route line and markers are the subject, the basemap is not.
const MAP_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry',            stylers: [{ color: '#f6f7f9' }] },
  { elementType: 'labels.icon',         stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill',    stylers: [{ color: '#8a94a6' }] },
  { elementType: 'labels.text.stroke',  stylers: [{ color: '#ffffff' }] },
  { featureType: 'administrative',      elementType: 'geometry', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi',                 stylers: [{ visibility: 'off' }] },
  { featureType: 'road',                elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.arterial',       elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.highway',        elementType: 'geometry', stylers: [{ color: '#eef1f5' }] },
  { featureType: 'road',                elementType: 'labels',   stylers: [{ visibility: 'simplified' }] },
  { featureType: 'transit',             stylers: [{ visibility: 'off' }] },
  { featureType: 'water',               elementType: 'geometry', stylers: [{ color: '#dbe6f0' }] },
]

// Live trainer marker: a small top-down car in a badge, rotated to the last
// known bearing so heading reads at a glance. Built as an inline SVG data URI
// because google.maps.Symbol can't rotate a multi-part glyph.
//
// The glide animation re-renders this every frame (~60fps) with a slightly
// different bearing, so a naive version would re-encode a fresh SVG + base64
// string dozens of times a second for a rotation nobody can actually perceive
// at that granularity. Rounding to the nearest 5° and caching by (bearing,
// color) turns that into a handful of reused icon objects per turn instead.
const CAR_ICON_CACHE = new Map<string, google.maps.Icon>()
function carIcon(bearing: number, color = '#2563eb'): google.maps.Icon {
  const rounded = Math.round(bearing / 5) * 5
  const key = `${rounded}:${color}`
  const cached = CAR_ICON_CACHE.get(key)
  if (cached) return cached

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38">
  <circle cx="19" cy="19" r="13.5" fill="${color}" stroke="#ffffff" stroke-width="3"/>
  <g transform="rotate(${rounded} 19 19)">
    <rect x="14" y="11" width="10" height="16" rx="3.2" fill="#ffffff"/>
    <path d="M15.6 15.2h6.8l-.9-2.2a1.6 1.6 0 0 0-1.5-1h-2a1.6 1.6 0 0 0-1.5 1z" fill="${color}"/>
    <rect x="15.6" y="19.4" width="6.8" height="4.4" rx="1.2" fill="${color}" opacity="0.55"/>
  </g>
</svg>`
  const icon: google.maps.Icon = {
    // base64 rather than percent-encoding — Maps renders it reliably in every browser.
    url: `data:image/svg+xml;base64,${btoa(svg)}`,
    scaledSize: new google.maps.Size(38, 38),
    anchor: new google.maps.Point(19, 19),
  }
  CAR_ICON_CACHE.set(key, icon)
  return icon
}

// ── Live marker animation ────────────────────────────────────────────────────
// Shortest-path angle interpolation so the car rotates the short way round
// (e.g. 350° → 10° turns through 20°, not backwards through 340°).
function angleLerp(a: number, b: number, t: number) {
  const diff = ((b - a + 540) % 360) - 180
  return a + diff * t
}

/** Glides the live trainer marker from wherever it last was to the newest GPS
 * point over `duration` ms instead of snapping there — the car reads as
 * continuously driving between polls rather than teleporting every refresh. */
function useAnimatedPosition(
  target: { lat: number; lng: number } | null,
  bearing: number,
  active: boolean,
  duration: number,
) {
  const [state, setState] = useState<{ pos: { lat: number; lng: number } | null; bearing: number }>(
    () => ({ pos: target, bearing }),
  )
  const stateRef = useRef(state)
  stateRef.current = state
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null }

    if (!target || !active) { setState({ pos: target, bearing }); return }

    const from = stateRef.current.pos ?? target
    const fromBearing = stateRef.current.bearing ?? bearing
    if (from.lat === target.lat && from.lng === target.lng && fromBearing === bearing) return

    const start = performance.now()
    const tick = () => {
      const t = Math.min(1, (performance.now() - start) / duration)
      const eased = 1 - (1 - t) * (1 - t) // ease-out — starts brisk, settles in
      setState({
        pos: {
          lat: from.lat + (target.lat - from.lat) * eased,
          lng: from.lng + (target.lng - from.lng) * eased,
        },
        bearing: angleLerp(fromBearing, bearing, eased),
      })
      rafRef.current = t < 1 ? requestAnimationFrame(tick) : null
    }
    rafRef.current = requestAnimationFrame(tick)

    return () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.lat, target?.lng, bearing, active, duration])

  return state
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const R_EARTH_KM = 6371
function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R_EARTH_KM * Math.asin(Math.sqrt(s))
}

function fmtDuration(sec: number) {
  if (!Number.isFinite(sec) || sec <= 0) return '0m'
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = Math.floor(sec % 60)
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`
}

function fmtTime(iso?: string | null) {
  if (!iso) return '—'
  const d = new Date(String(iso).replace(' ', 'T'))
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function secondsSince(iso?: string | null) {
  if (!iso) return null
  const then = new Date(String(iso).replace(' ', 'T')).getTime()
  if (Number.isNaN(then)) return null
  return Math.max(0, Math.floor((Date.now() - then) / 1000))
}

function secondsBetween(a: string, b: string) {
  const ta = new Date(a.replace(' ', 'T')).getTime()
  const tb = new Date(b.replace(' ', 'T')).getTime()
  if (Number.isNaN(ta) || Number.isNaN(tb)) return 0
  return (tb - ta) / 1000
}

const midpoint = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) =>
  ({ lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 })

function timeAgo(iso?: string | null) {
  const sec = secondsSince(iso)
  return sec === null ? '—' : fmtDuration(sec)
}

const initials = (name?: string | null) =>
  (name ?? '').trim().split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('') || '—'

// in_session / en_route / pickup … → readable + colour
const PHASE_STYLE: Record<string, { label: string; cls: string; dot: string }> = {
  in_session:  { label: 'In session',  cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' },
  en_route:    { label: 'En route',    cls: 'bg-blue-50 text-blue-700 ring-blue-200',          dot: 'bg-blue-500'    },
  travelling:  { label: 'Travelling',  cls: 'bg-blue-50 text-blue-700 ring-blue-200',          dot: 'bg-blue-500'    },
  pickup:      { label: 'Pickup',      cls: 'bg-amber-50 text-amber-700 ring-amber-200',       dot: 'bg-amber-500'   },
  reached:     { label: 'Reached',     cls: 'bg-violet-50 text-violet-700 ring-violet-200',    dot: 'bg-violet-500'  },
  completed:   { label: 'Completed',   cls: 'bg-gray-50 text-gray-600 ring-gray-200',          dot: 'bg-gray-400'    },
}
const phaseOf = (p?: string) =>
  PHASE_STYLE[String(p ?? '').toLowerCase()]
  ?? { label: String(p ?? '—').replace(/_/g, ' '), cls: 'bg-gray-50 text-gray-600 ring-gray-200', dot: 'bg-gray-400' }

// Session statuses that mean "the trainer has not begun this session yet", so
// there is nothing to draw on the map — the page says so instead of showing an
// empty viewport that looks like a failure.
const NOT_STARTED_STATUSES = new Set([
  'PENDING', 'UPCOMING', 'ACCEPTED', 'CONFIRMED', 'REQUESTED', 'RESCHEDULED', 'NEW',
])
const statusTone = (s?: string) => {
  const v = String(s ?? '').toUpperCase()
  if (v === 'COMPLETED') return { chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' }
  if (v === 'CANCELLED' || v === 'REJECTED') return { chip: 'bg-red-50 text-red-600 ring-red-200', dot: 'bg-red-500' }
  if (v === 'ONGOING' || v === 'STARTED' || v === 'IN_PROGRESS') return { chip: 'bg-blue-50 text-blue-700 ring-blue-200', dot: 'bg-blue-500' }
  if (NOT_STARTED_STATUSES.has(v)) return { chip: 'bg-amber-50 text-amber-700 ring-amber-200', dot: 'bg-amber-400' }
  return { chip: 'bg-gray-50 text-gray-600 ring-gray-200', dot: 'bg-gray-300' }
}
const prettyStatus = (s?: string) => String(s ?? '—').replace(/_/g, ' ')

const ordinal = (n: number) => `${n}${['st', 'nd', 'rd'][n - 1] || 'th'}`
const sessionLabel = (s: BookingSession, i: number) =>
  Array.isArray(s.session_no)
    ? s.session_no.map(ordinal).join(' & ')
    : ordinal(Number(s.session_no ?? i + 1))

// ── API shapes we only touch here ────────────────────────────────────────────
interface BookingSession {
  id: number
  session_no?: number | number[]
  start_time?: string | null
  end_time?: string | null
  session_status?: string
  session_type?: string
}
interface BookingRow {
  id: number
  booking_status?: string
  payment_status?: string
  created_at?: string
  no_of_sessions?: number
  user?: { id?: number; name?: string } | null
  driver?: { id?: number; name?: string } | null
  sessions?: BookingSession[]
}

// ── Primitives ───────────────────────────────────────────────────────────────
function Chip({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset', className)}>
      {children}
    </span>
  )
}

/** Glass metric shown on the map rail. */
function Metric({ icon: Icon, label, value, tone = 'slate' }: {
  icon: typeof Gauge; label: string; value: string; tone?: 'slate' | 'blue' | 'green' | 'amber'
}) {
  const tones = {
    slate: 'text-slate-500',
    blue:  'text-blue-600',
    green: 'text-emerald-600',
    amber: 'text-amber-600',
  }
  return (
    <div className="flex items-center gap-2 px-3 py-2 first:ps-3.5 last:pe-3.5 border-e border-white/60 last:border-e-0 min-w-max">
      <Icon size={14} strokeWidth={2.5} className={cn('flex-shrink-0', tones[tone])}/>
      <div className="leading-tight">
        <div className="text-[9px] font-bold uppercase tracking-[0.08em] text-slate-400">{label}</div>
        <div className="text-[13px] font-bold text-slate-800 tabular-nums">{value}</div>
      </div>
    </div>
  )
}

/** Persistent caption pinned above a map point — Start / Stop / Trainer / Student
 * read at a glance on the map itself instead of only in the bottom legend. A plain
 * white pill with dark text; no separate colored dot/circle underneath it. */
function MapLabel({ position, children }: {
  position: { lat: number; lng: number }; children: React.ReactNode
}) {
  return (
    // OverlayView's pane sits above the marker layer, so an 8px gap (fine for
    // Start/Student, which have no marker underneath) let this pill's bottom
    // edge cut across the top third of the 38px-tall car icon at the trainer's
    // position — the exact spot the car marker was "missing" from. 24px clears
    // the icon's 19px radius with a small margin regardless of what's under it.
    <OverlayView position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -(h + 24) })}>
      <div className="pointer-events-none whitespace-nowrap rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-slate-800 shadow-md ring-1 ring-black/10">
        {children}
      </div>
    </OverlayView>
  )
}

/** Round icon button used for the map controls. */
function MapBtn({ icon: Icon, label, onClick, active, disabled }: {
  icon: typeof Gauge; label: string; onClick: () => void; active?: boolean; disabled?: boolean
}) {
  return (
    <button
      type="button" onClick={onClick} disabled={disabled} title={label} aria-label={label}
      aria-pressed={active}
      className={cn(
        'w-9 h-9 rounded-xl flex items-center justify-center shadow-sm ring-1 transition-colors backdrop-blur',
        disabled ? 'bg-white/70 text-slate-300 ring-black/5 cursor-not-allowed'
          : active ? 'bg-slate-900 text-white ring-slate-900'
          : 'bg-white/90 text-slate-600 ring-black/5 hover:bg-white hover:text-slate-900',
      )}>
      <Icon size={15} strokeWidth={2.5}/>
    </button>
  )
}

/** Person pill in the map header — avatar + role + name, click through to profile. */
function PersonPill({ role, name, icon: Icon, tone, onClick }: {
  role: string; name?: string | null; icon: typeof User; tone: 'teal' | 'brand'; onClick?: () => void
}) {
  const tones = {
    teal:  'bg-teal-50 text-teal-700 ring-teal-100',
    brand: 'bg-brand-50 text-brand-600 ring-brand-100',
  }
  return (
    <button
      type="button" onClick={onClick} disabled={!onClick}
      className="group flex items-center gap-2 rounded-xl px-1.5 py-1 -mx-1.5 transition-colors enabled:hover:bg-slate-50 disabled:cursor-default">
      <span className={cn('w-8 h-8 rounded-xl ring-1 ring-inset flex items-center justify-center text-[11px] font-black', tones[tone])}>
        {name?.trim() ? initials(name) : <Icon size={13} strokeWidth={2.5}/>}
      </span>
      <span className="text-start leading-tight">
        <span className="block text-[9px] font-bold uppercase tracking-[0.08em] text-slate-400">{role}</span>
        <span className={cn('block text-xs font-bold text-slate-700 max-w-[130px] truncate', onClick && 'group-hover:text-brand-500')}>
          {name?.trim() || '—'}
        </span>
      </span>
    </button>
  )
}

/** Live session card in the navigator's "Live now" tab. */
function LiveCard({ s, active, onClick }: { s: ActiveSession; active: boolean; onClick: () => void }) {
  const phase = phaseOf(s.phase)
  const speed = Math.round(s.trainer?.speed ?? 0)
  return (
    <button
      onClick={onClick}
      className={cn(
        'relative w-full text-start rounded-2xl p-3 transition-all duration-200 ring-1',
        active
          ? 'bg-slate-900 text-white ring-slate-900 shadow-lg shadow-slate-900/10'
          : 'bg-white ring-slate-100 hover:ring-slate-200 hover:shadow-sm',
      )}
    >
      <div className="flex items-center gap-2">
        <span className="relative flex-shrink-0">
          <span className={cn('block w-2 h-2 rounded-full', phase.dot)}/>
          <span className={cn('absolute inset-0 rounded-full animate-ping', phase.dot, 'opacity-60')}/>
        </span>
        <span className={cn('text-xs font-black tracking-tight', active ? 'text-white' : 'text-slate-800')}>
          Booking #{s.booking_id}
        </span>
        <span className={cn('ms-auto text-[10px] font-bold uppercase tracking-wide',
          active ? 'text-white/70' : 'text-slate-400')}>{phase.label}</span>
      </div>

      <div className={cn('mt-2 flex items-center gap-1.5 text-[11px] font-semibold',
        active ? 'text-white/80' : 'text-slate-500')}>
        <User size={11} strokeWidth={2.5}/>
        <span className="truncate">{s.trainer?.name?.trim() || (s.trainer?.id ? `Trainer #${s.trainer.id}` : 'Trainer —')}</span>
      </div>

      <div className="mt-2.5 flex items-end justify-between gap-2">
        <span className="leading-none">
          <span className={cn('text-xl font-black tabular-nums', active ? 'text-white' : 'text-slate-900')}>{speed}</span>
          <span className={cn('ms-1 text-[10px] font-bold', active ? 'text-white/60' : 'text-slate-400')}>km/h</span>
        </span>
        <span className={cn('flex items-center gap-1 text-[10px] font-bold', active ? 'text-white/60' : 'text-slate-400')}>
          <Clock size={10} strokeWidth={2.5}/>{timeAgo(s.started_at)}
        </span>
      </div>

      {/* Speed bar — a glanceable read across the whole list (capped at 120 km/h) */}
      <div className={cn('mt-2 h-1 rounded-full overflow-hidden', active ? 'bg-white/15' : 'bg-slate-100')}>
        <span
          className={cn('block h-full rounded-full transition-all duration-500',
            active ? 'bg-white' : speed > 80 ? 'bg-red-400' : speed > 0 ? 'bg-emerald-500' : 'bg-slate-300')}
          style={{ width: `${Math.min(100, (speed / 120) * 100)}%` }}
        />
      </div>
    </button>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────
type NavTab = 'live' | 'bookings'

export function BookingTrackingPage() {
  const navigate = useNavigate()
  // Deep link from the booking details page: ?booking_id=… (&booking_session_id=…)
  const [params] = useSearchParams()
  const deepBookingId = Number(params.get('booking_id')) || null
  const deepSessionId = Number(params.get('booking_session_id')) || null

  const [search, setSearch] = useState(deepBookingId ? String(deepBookingId) : '')
  const [submittedSearch, setSubmittedSearch] = useState(deepBookingId ? String(deepBookingId) : '')
  const [bookingPage, setBookingPage] = useState(1)
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(deepBookingId)
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(deepSessionId)
  // Set when a session is picked from the live list — we already know its tracking
  // id there, so the resolve-by-session lookup can be skipped.
  const [trackingHint, setTrackingHint] = useState<number | null>(null)
  const [live, setLive] = useState(true)
  const [navTab, setNavTab] = useState<NavTab>(deepBookingId ? 'bookings' : 'live')
  const [follow, setFollow] = useState(true)
  const [satellite, setSatellite] = useState(false)
  const mapRef = useRef<google.maps.Map | null>(null)
  // Only auto-fit the map once per session; refetches shouldn't yank the viewport
  // away from wherever the operator has panned to.
  const fittedFor = useRef<number | null>(null)

  const { isLoaded } = useJsApiLoader({
    id: 'drivys-google-maps',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  })

  // ── Live sessions (polls while Live is on) ──
  const activeQ = useQuery({
    queryKey: ['gps', 'active-sessions'],
    queryFn: () => gpsService.activeSessions({ page: 1, limit: 50 }),
    refetchInterval: live ? LIST_REFRESH_MS : false,
  })
  // Only sessions happening right now belong in live tracking — the endpoint can
  // still hand back a row whose phase has moved to completed, and that is history,
  // not a live feed. Memoised so liveBySessionId / the effects below see a stable
  // array identity across renders.
  const activeSessions = useMemo(
    () => (activeQ.data?.sessions ?? []).filter(s => String(s.phase ?? '').toLowerCase() !== 'completed'),
    [activeQ.data],
  )
  // booking_session_id → live row, so booking sessions can show a Live badge.
  const liveBySessionId = useMemo(
    () => new Map(activeSessions.map(s => [s.booking_session_id, s])),
    [activeSessions],
  )

  // ── Bookings list ──
  const bookingsQ = useQuery({
    queryKey: ['tracking', 'bookings', bookingPage, submittedSearch],
    queryFn: () => bookingService.list({
      page: bookingPage, limit: BOOKING_PAGE_SIZE, search: submittedSearch || undefined,
    }),
    staleTime: 30_000,
  })
  const bookingsRaw = bookingsQ.data as Record<string, any> | undefined
  const bookings: BookingRow[] = bookingsRaw?.data?.bookings ?? bookingsRaw?.bookings ?? bookingsRaw?.data ?? []
  const bookingTotal    = bookingsRaw?.total ?? bookingsRaw?.data?.total_count ?? bookingsRaw?.total_count ?? bookings.length
  const bookingLastPage = Math.max(1, Math.ceil(bookingTotal / BOOKING_PAGE_SIZE))

  // ── Sessions of the selected booking ──
  const bookingQ = useQuery({
    queryKey: ['tracking', 'booking', selectedBookingId],
    queryFn: () => bookingService.getById(selectedBookingId!),
    enabled: !!selectedBookingId,
  })
  const bookingDetail: BookingRow | null = (bookingQ.data as any)?.data ?? (bookingQ.data as any) ?? null
  const sessions: BookingSession[] = useMemo(() => bookingDetail?.sessions ?? [], [bookingDetail])
  const selectedSession = sessions.find(s => s.id === selectedSessionId) ?? null
  // Present in the active feed = the session is happening right now. Anything else
  // (completed, cancelled, not yet started) is history: fetched once, never polled.
  const liveRow = selectedSessionId !== null ? liveBySessionId.get(selectedSessionId) ?? null : null
  const sessionIsCurrent = !!liveRow

  // Auto-pick the session that is live right now, else the first one, so the
  // panel is never left empty after choosing a booking.
  useEffect(() => {
    if (!sessions.length) return
    if (selectedSessionId !== null && sessions.some(s => s.id === selectedSessionId)) return
    const liveOne = sessions.find(s => liveBySessionId.has(s.id))
    setTrackingHint(liveOne ? liveBySessionId.get(liveOne.id)!.tracking_session_id : null)
    setSelectedSessionId((liveOne ?? sessions[0]).id)
    fittedFor.current = null
  }, [sessions, selectedSessionId, liveBySessionId])

  // ── Resolve the tracking session for the chosen booking session ──
  const trackingQ = useQuery({
    queryKey: ['gps', 'search', 'by-session', selectedSessionId],
    queryFn: () => gpsService.search({ booking_session_id: selectedSessionId!, limit: 5 }),
    enabled: !!selectedSessionId && trackingHint === null,
  })
  const resolvedTracking = trackingHint
    ?? liveBySessionId.get(selectedSessionId ?? -1)?.tracking_session_id
    ?? trackingQ.data?.sessions?.[0]?.tracking_session_id
    ?? null
  const trackingRow = trackingQ.data?.sessions?.[0] ?? null
  const resolvingTracking = !!selectedSessionId && trackingHint === null && trackingQ.isLoading

  // ── Selected tracking session detail + route ──
  const detailQ = useQuery({
    queryKey: ['gps', 'session', resolvedTracking],
    queryFn: () => gpsService.sessionDetail(resolvedTracking!),
    enabled: !!resolvedTracking,
    // Poll only a session that is happening right now; a finished one can't change,
    // so re-requesting it is pure noise.
    refetchInterval: q => (
      live && sessionIsCurrent && (q.state.data as SessionDetail | undefined)?.is_active !== false
        ? TRACK_REFRESH_MS : false
    ),
  })
  const detail = detailQ.data
  const sessionEnded = detail?.is_active === false
  // Live UI (car marker, current speed, stale-GPS alarm) applies to in-progress
  // sessions only — a completed session is a recorded route. The active-sessions
  // feed is paginated, so a session missing from it can still be live: trust the
  // session detail's own is_active flag as well.
  const showLive = (sessionIsCurrent || detail?.is_active === true) && !sessionEnded

  const routeQ = useQuery({
    queryKey: ['gps', 'route', resolvedTracking],
    queryFn: () => gpsService.sessionRoute(resolvedTracking!),
    enabled: !!resolvedTracking,
    refetchInterval: live && showLive ? TRACK_REFRESH_MS : false,
  })

  const points: RoutePoint[] = useMemo(
    () => (routeQ.data ?? []).filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng) && !(p.lat === 0 && p.lng === 0)),
    [routeQ.data],
  )
  const path = useMemo(() => points.map(p => ({ lat: p.lat, lng: p.lng })), [points])
  const last = points.length ? points[points.length - 1] : null

  // One pass over the points covers both the route-drawing split and the distance
  // total: consecutive points further apart in time than a normal GPS ping mean
  // the signal dropped in between, so that stretch is neither drawn as a line nor
  // counted as travelled distance (the trainer didn't teleport in a straight line).
  const { segments, gaps, distanceKm } = useMemo(() => {
    const segments: { lat: number; lng: number }[][] = []
    const gaps: { from: RoutePoint; to: RoutePoint; gapSec: number }[] = []
    let current: { lat: number; lng: number }[] = []
    let distanceKm = 0
    for (let i = 0; i < points.length; i++) {
      const p = points[i]
      if (i > 0) {
        const gapSec = secondsBetween(points[i - 1].recorded_at, p.recorded_at)
        if (gapSec > ROUTE_GAP_SEC) {
          gaps.push({ from: points[i - 1], to: p, gapSec })
          segments.push(current)
          current = []
        } else {
          distanceKm += haversineKm(points[i - 1], p)
        }
      }
      current.push({ lat: p.lat, lng: p.lng })
    }
    if (current.length) segments.push(current)
    return { segments, gaps, distanceKm }
  }, [points])

  // Position of the trainer marker: their current spot on a live session, the final
  // recorded point on a finished one.
  const livePos = useMemo(() => (
    last
      ? { lat: last.lat, lng: last.lng }
      : liveRow?.trainer && liveRow.trainer.lat
        ? { lat: liveRow.trainer.lat, lng: liveRow.trainer.lng }
        : null
  ), [last, liveRow])

  // Smoothly glide the car marker toward each new GPS fix instead of snapping to
  // it — animates only while the session is actually live; a finished session's
  // end marker is fixed and never eases.
  const animatedTrainer = useAnimatedPosition(livePos, last?.bearing ?? 0, showLive, Math.max(2000, TRACK_REFRESH_MS - 1000))
  const trainerPos = animatedTrainer.pos ?? livePos

  // A one-time placement for whichever session is selected — every later camera
  // move comes from the follow/fit-bounds effects below (both animated), so the
  // map's `center` prop never fights a manual pan by re-snapping on every poll.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialCenter = useMemo(() => livePos ?? DEFAULT_CENTER, [resolvedTracking])

  const stats = useMemo(() => {
    let maxSpeed = 0
    let speedSum = 0
    for (let i = 0; i < points.length; i++) {
      maxSpeed = Math.max(maxSpeed, points[i].speed ?? 0)
      speedSum += points[i].speed ?? 0
    }
    return {
      distanceKm,
      maxSpeed,
      avgSpeed: points.length ? speedSum / points.length : 0,
      count: points.length,
    }
  }, [points, distanceKm])

  // Frame the whole route the first time a session's points arrive.
  useEffect(() => {
    const m = mapRef.current
    if (!m || !path.length || fittedFor.current === resolvedTracking) return
    const b = new google.maps.LatLngBounds()
    path.forEach(p => b.extend(p))
    m.fitBounds(b, 60)
    fittedFor.current = resolvedTracking
  }, [path, resolvedTracking])

  // Follow mode: keep the moving trainer centred on a live session. Off by default
  // once the operator turns it off, so panning around is never fought by a refetch.
  useEffect(() => {
    if (!follow || !showLive || !livePos) return
    mapRef.current?.panTo(livePos)
  }, [follow, showLive, livePos])

  const selectBooking = (id: number) => {
    setSelectedBookingId(id)
    setSelectedSessionId(null)      // the effect above picks the live/first session
    setTrackingHint(null)
    fittedFor.current = null
  }
  const selectSession = (sessionId: number, hint: number | null = null) => {
    setSelectedSessionId(sessionId)
    setTrackingHint(hint)
    fittedFor.current = null
  }
  const selectLive = (s: ActiveSession) => {
    setSelectedBookingId(s.booking_id)
    selectSession(s.booking_session_id, s.tracking_session_id)
  }

  const recenter = useCallback(() => {
    const m = mapRef.current
    if (!m) return
    if (path.length) {
      const b = new google.maps.LatLngBounds()
      path.forEach(p => b.extend(p))
      m.fitBounds(b, 60)
    } else if (livePos) {
      m.panTo(livePos); m.setZoom(15)
    }
  }, [path, livePos])

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmittedSearch(search.trim())
    setBookingPage(1)
  }
  const clearSearch = () => { setSearch(''); setSubmittedSearch(''); setBookingPage(1) }

  const phase = phaseOf(detail?.phase ?? liveRow?.phase ?? trackingRow?.phase)

  // Device stopped reporting while the session is still open — worth flagging,
  // otherwise a frozen marker looks the same as a stationary trainer.
  const lastPointAgeSec = secondsSince(last?.recorded_at)
  const gpsStale = showLive && lastPointAgeSec !== null && lastPointAgeSec > STALE_GPS_SEC

  const sessionStatus  = String(selectedSession?.session_status ?? '').toUpperCase()
  const notStartedYet  = !resolvedTracking && !resolvingTracking && !!selectedSession
    && (NOT_STARTED_STATUSES.has(sessionStatus) || (secondsSince(selectedSession.start_time) ?? -1) < 0)
  const noTrackingData = !resolvedTracking && !resolvingTracking && !!selectedSession && !notStartedYet
  const showMap = !!resolvedTracking && !resolvingTracking

  const trainerName = detail?.trainer_name ?? bookingDetail?.driver?.name
  const studentName = detail?.student_name ?? bookingDetail?.user?.name
  const currentSpeed = Math.round(last?.speed ?? liveRow?.trainer?.speed ?? 0)

  return (
    <div className="flex flex-col gap-4">

      {/* ══ Console: navigator + map stage ═══════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 lg:h-[calc(100vh-9rem)] lg:min-h-[620px]">

        {/* ── Navigator ── */}
        <div className="flex flex-col overflow-hidden rounded-3xl bg-white ring-1 ring-slate-200/70 shadow-sm">

          {/* Tabs */}
          <div className="p-2.5 pb-0">
            <div className="flex items-center gap-1 rounded-2xl bg-slate-100/80 p-1">
              {([
                { key: 'live',     label: 'Live now',     count: activeSessions.length },
                { key: 'bookings', label: 'All bookings', count: bookingTotal },
              ] as { key: NavTab; label: string; count: number }[]).map(o => (
                <button
                  key={o.key} type="button" onClick={() => setNavTab(o.key)}
                  className={cn('flex flex-1 items-center justify-center gap-1.5 rounded-xl px-2 py-1.5 text-[11px] font-bold transition-all',
                    navTab === o.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
                  {o.key === 'live' && (
                    <span className={cn('w-1.5 h-1.5 rounded-full', o.count ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300')}/>
                  )}
                  {o.label}
                  <span className={cn('rounded-full px-1.5 text-[10px] font-black tabular-nums',
                    navTab === o.key ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-500')}>{o.count}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Live now tab ── */}
          {navTab === 'live' ? (
            <div className="flex-1 overflow-y-auto p-2.5 space-y-2">
              {activeQ.isLoading ? (
                <div className="flex justify-center py-12"><Spinner/></div>
              ) : activeSessions.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
                  <span className="w-12 h-12 rounded-2xl bg-slate-50 ring-1 ring-slate-100 flex items-center justify-center">
                    <Radio size={20} className="text-slate-300"/>
                  </span>
                  <p className="text-xs font-bold text-slate-600">Nothing live right now</p>
                  <p className="text-[11px] text-slate-400">Sessions appear here the moment a trainer starts one.</p>
                  <button onClick={() => setNavTab('bookings')}
                    className="mt-1 text-[11px] font-bold text-brand-500 hover:underline">Browse bookings instead</button>
                </div>
              ) : activeSessions.map(s => (
                <LiveCard
                  key={s.tracking_session_id}
                  s={s}
                  active={s.booking_session_id === selectedSessionId}
                  onClick={() => selectLive(s)}
                />
              ))}
            </div>
          ) : selectedBookingId === null ? (
            /* ── Bookings tab: level 1 ── */
            <>
              <form onSubmit={submitSearch} className="px-2.5 pt-2.5">
                <div className="relative">
                  <Search size={14} strokeWidth={2.5} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search booking, student or trainer…"
                    className="w-full rounded-2xl bg-slate-50 py-2.5 ps-9 pe-9 text-xs font-semibold text-slate-700 ring-1 ring-inset ring-slate-200 placeholder:font-medium placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                  {(search || submittedSearch) && (
                    <button type="button" onClick={clearSearch} aria-label="Clear search"
                      className="absolute end-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      <X size={14} strokeWidth={2.5}/>
                    </button>
                  )}
                </div>
              </form>

              <div className="flex-1 overflow-y-auto p-2.5 space-y-1.5">
                {bookingsQ.isLoading ? (
                  <div className="flex justify-center py-12"><Spinner/></div>
                ) : bookings.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
                    <span className="w-12 h-12 rounded-2xl bg-slate-50 ring-1 ring-slate-100 flex items-center justify-center">
                      <SlidersHorizontal size={20} className="text-slate-300"/>
                    </span>
                    <p className="text-xs font-bold text-slate-600">No bookings match</p>
                    <p className="text-[11px] text-slate-400">Try a different booking id or name.</p>
                  </div>
                ) : bookings.map(b => {
                  const tone = statusTone(b.booking_status)
                  const liveCount = (b.sessions ?? []).filter(s => liveBySessionId.has(s.id)).length
                  return (
                    <button
                      key={b.id}
                      onClick={() => selectBooking(b.id)}
                      className="group w-full text-start rounded-2xl px-3 py-2.5 ring-1 ring-transparent transition-all hover:bg-slate-50 hover:ring-slate-200"
                    >
                      <div className="flex items-center gap-2">
                        <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', tone.dot)}/>
                        <span className="text-xs font-black tracking-tight text-slate-800">Booking #{b.id}</span>
                        {!!liveCount && (
                          <Chip className="bg-emerald-50 text-emerald-700 ring-emerald-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"/>Live
                          </Chip>
                        )}
                        <ChevronRight size={14} className="ms-auto text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-500"/>
                      </div>
                      <div className="mt-1.5 flex items-center gap-3 text-[11px] font-semibold text-slate-500">
                        <span className="flex items-center gap-1 min-w-0">
                          <GraduationCap size={11} strokeWidth={2.5} className="text-brand-400 flex-shrink-0"/>
                          <span className="truncate">{b.user?.name?.trim() || '—'}</span>
                        </span>
                        <span className="flex items-center gap-1 min-w-0">
                          <User size={11} strokeWidth={2.5} className="text-teal-500 flex-shrink-0"/>
                          <span className="truncate">{b.driver?.name?.trim() || '—'}</span>
                        </span>
                      </div>
                      <div className="mt-1.5">
                        <Chip className={tone.chip}>{prettyStatus(b.booking_status)}</Chip>
                      </div>
                    </button>
                  )
                })}
              </div>

              {bookingLastPage > 1 && (
                <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2.5">
                  <button
                    onClick={() => setBookingPage(p => Math.max(1, p - 1))}
                    disabled={bookingPage <= 1}
                    className="flex items-center gap-1 rounded-xl px-2 py-1 text-[11px] font-bold text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent">
                    <ChevronLeft size={12}/> Prev
                  </button>
                  <span className="text-[11px] font-bold tabular-nums text-slate-400">{bookingPage} / {bookingLastPage}</span>
                  <button
                    onClick={() => setBookingPage(p => Math.min(bookingLastPage, p + 1))}
                    disabled={bookingPage >= bookingLastPage}
                    className="flex items-center gap-1 rounded-xl px-2 py-1 text-[11px] font-bold text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent">
                    Next <ChevronRight size={12}/>
                  </button>
                </div>
              )}
            </>
          ) : (
            /* ── Bookings tab: level 2 — sessions of one booking ── */
            <>
              <div className="border-b border-slate-100 px-3 py-2.5">
                <button
                  onClick={() => { setSelectedBookingId(null); setSelectedSessionId(null); setTrackingHint(null) }}
                  className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 hover:text-slate-900">
                  <ArrowLeft size={13} strokeWidth={2.5}/> All bookings
                </button>
                <div className="mt-2 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-black tracking-tight text-slate-900">Booking #{selectedBookingId}</div>
                    <div className="mt-0.5 truncate text-[11px] font-semibold text-slate-500">
                      {bookingDetail?.user?.name?.trim() || '—'} · {bookingDetail?.driver?.name?.trim() || '—'}
                    </div>
                  </div>
                  <Chip className={statusTone(bookingDetail?.booking_status).chip}>
                    {prettyStatus(bookingDetail?.booking_status)}
                  </Chip>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-3 py-3">
                {bookingQ.isLoading ? (
                  <div className="flex justify-center py-12"><Spinner/></div>
                ) : sessions.length === 0 ? (
                  <p className="py-12 text-center text-xs font-bold text-slate-400">This booking has no sessions</p>
                ) : (
                  /* Vertical timeline — connector + status dot per session */
                  <ol className="relative">
                    <span className="absolute inset-y-2 start-[7px] w-px bg-slate-100" aria-hidden/>
                    {sessions.map((s, i) => {
                      const isLive = liveBySessionId.has(s.id)
                      const isSel  = s.id === selectedSessionId
                      const tone   = statusTone(s.session_status)
                      return (
                        <li key={s.id} className="relative ps-6">
                          <span className={cn('absolute start-0 top-3.5 flex w-[15px] h-[15px] items-center justify-center rounded-full bg-white ring-2',
                            isSel ? 'ring-slate-900' : 'ring-slate-200')}>
                            <span className={cn('w-1.5 h-1.5 rounded-full', tone.dot, isLive && 'animate-pulse')}/>
                          </span>
                          <button
                            onClick={() => selectSession(s.id, liveBySessionId.get(s.id)?.tracking_session_id ?? null)}
                            className={cn('my-1 w-full text-start rounded-2xl px-3 py-2.5 transition-all ring-1',
                              isSel ? 'bg-slate-900 ring-slate-900 shadow-lg shadow-slate-900/10'
                                    : 'bg-white ring-slate-100 hover:ring-slate-200 hover:bg-slate-50')}>
                            <div className="flex items-center gap-2">
                              <span className={cn('text-xs font-black tracking-tight', isSel ? 'text-white' : 'text-slate-800')}>
                                {sessionLabel(s, i)} session
                              </span>
                              {isLive && (
                                <Chip className={isSel
                                  ? 'bg-emerald-400/20 text-emerald-300 ring-emerald-400/30'
                                  : 'bg-emerald-50 text-emerald-700 ring-emerald-200'}>
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"/>Live
                                </Chip>
                              )}
                            </div>
                            <div className={cn('mt-1 flex items-center gap-1.5 text-[11px] font-semibold',
                              isSel ? 'text-white/70' : 'text-slate-500')}>
                              <Calendar size={11} strokeWidth={2.5}/>{fmtTime(s.start_time)}
                            </div>
                            <div className="mt-1.5 flex items-center gap-1.5">
                              <Chip className={isSel ? 'bg-white/10 text-white/80 ring-white/20' : tone.chip}>
                                {prettyStatus(s.session_status)}
                              </Chip>
                              <span className={cn('text-[10px] font-bold uppercase tracking-wide',
                                isSel ? 'text-white/40' : 'text-slate-300')}>#{s.id}</span>
                            </div>
                          </button>
                        </li>
                      )
                    })}
                  </ol>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Map stage ── */}
        <div className="relative overflow-hidden rounded-3xl bg-slate-100 ring-1 ring-slate-200/70 shadow-sm min-h-[520px] lg:min-h-0">

          {/* Map / state layer */}
          {!selectedSessionId ? (
            <StageMessage
              icon={<MapPin size={22} className="text-slate-400"/>}
              title="Nothing selected yet"
              body="Pick a live session on the left for real-time tracking, or open a booking to review a recorded route."
            />
          ) : notStartedYet ? (
            <StageMessage
              tone="amber"
              icon={<CalendarClock size={22} className="text-amber-600"/>}
              title="This session has not started yet"
              body={`Tracking begins the moment the trainer starts the session. Scheduled for ${fmtTime(selectedSession?.start_time)}.`}
              footer={<Chip className={statusTone(selectedSession?.session_status).chip}>{prettyStatus(selectedSession?.session_status)}</Chip>}
            />
          ) : noTrackingData ? (
            <StageMessage
              icon={<AlertTriangle size={22} className="text-slate-400"/>}
              title="No tracking recorded for this session"
              body={`The session is ${prettyStatus(selectedSession?.session_status).toLowerCase()}, but no GPS trail was captured for it.`}
            />
          ) : !GOOGLE_MAPS_API_KEY ? (
            <StageMessage
              icon={<Layers size={22} className="text-slate-400"/>}
              title="Google Maps API key not configured"
              body="Set VITE_APP_GOOGLE_API_KEY to enable tracking."
            />
          ) : !isLoaded || resolvingTracking ? (
            <div className="absolute inset-0 animate-pulse bg-slate-200/60"/>
          ) : (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={initialCenter}
              zoom={livePos ? 15 : 11}
              onLoad={m => { mapRef.current = m }}
              onUnmount={() => { mapRef.current = null }}
              onDragStart={() => setFollow(false)}   // manual pan wins over follow mode
              options={{
                streetViewControl: false, mapTypeControl: false, fullscreenControl: false, zoomControl: false,
                mapTypeId: satellite ? 'hybrid' : 'roadmap',
                styles: satellite ? undefined : MAP_STYLE,
                gestureHandling: 'greedy',
              }}
            >
              {segments.map((seg, i) => seg.length > 1 && (
                <Fragment key={i}>
                  {/* Casing under the route line keeps it readable over satellite / dense tiles */}
                  <Polyline path={seg} options={{ strokeColor: '#ffffff', strokeOpacity: 0.95, strokeWeight: 9, zIndex: 1 }}/>
                  <Polyline path={seg} options={{ strokeColor: showLive ? '#2563eb' : '#475569', strokeOpacity: 1, strokeWeight: 4, zIndex: 2 }}/>
                </Fragment>
              ))}

              {/* Signal-loss gaps: no line at all across them (the route really is
                  unknown for that stretch) — just a caption saying how long it lasted. */}
              {gaps.map((g, i) => (
                <MapLabel key={i} position={midpoint(g.from, g.to)}>
                  <span className="flex items-center gap-1 text-amber-700">
                    <AlertTriangle size={11}/> GPS lost · {fmtDuration(g.gapSec)}
                  </span>
                </MapLabel>
              ))}

              {path.length > 0 && (
                <MapLabel position={path[0]}>
                  Start · {fmtTime(points[0]?.recorded_at ?? detail?.started_at ?? liveRow?.started_at)}
                </MapLabel>
              )}

              {trainerPos && (
                <>
                  {/* Halo pulse under the live marker */}
                  {showLive && (
                    <Marker
                      position={trainerPos}
                      clickable={false}
                      zIndex={4}
                      icon={{
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 24, fillColor: '#2563eb', fillOpacity: 0.14, strokeColor: '#2563eb',
                        strokeOpacity: 0.25, strokeWeight: 1,
                      }}
                    />
                  )}
                  {/* Car icon marks the trainer's position either way — animated/live,
                      or parked at the final heading once the session has ended. */}
                  <Marker
                    position={trainerPos}
                    title={showLive ? (trainerName || 'Trainer') : 'Stop'}
                    zIndex={5}
                    icon={carIcon(showLive ? animatedTrainer.bearing : (last?.bearing ?? 0), showLive ? '#2563eb' : '#475569')}
                  />
                  <MapLabel position={trainerPos}>
                    {showLive
                      ? `Trainer${trainerName ? ' · ' + trainerName : ''} · ${currentSpeed} km/h`
                      : `Stop · ${fmtTime(detail?.ended_at ?? last?.recorded_at)}`}
                  </MapLabel>
                </>
              )}

              {showLive && liveRow?.student?.lat ? (
                <MapLabel position={{ lat: liveRow.student.lat, lng: liveRow.student.lng }}>
                  Student{studentName ? ` · ${studentName}` : ''}
                </MapLabel>
              ) : null}
            </GoogleMap>
          )}

          {/* ── Floating header: who / what / where ── */}
          {!!selectedSessionId && (
            <div className="pointer-events-none absolute inset-x-3 top-3 flex flex-wrap items-start gap-2">
              <div className="pointer-events-auto flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl bg-white/85 px-3.5 py-2.5 shadow-lg shadow-slate-900/5 ring-1 ring-black/5 backdrop-blur-xl">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-black tracking-tight text-slate-900">
                      Booking #{detail?.booking_id ?? selectedBookingId}
                    </span>
                    {showLive ? (
                      <Chip className="bg-emerald-50 text-emerald-700 ring-emerald-200">
                        <span className="relative flex w-1.5 h-1.5">
                          <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping"/>
                          <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-500"/>
                        </span>
                        Live
                      </Chip>
                    ) : sessionEnded ? (
                      <Chip className="bg-slate-100 text-slate-500 ring-slate-200">Recorded</Chip>
                    ) : null}
                    {showMap && <Chip className={phase.cls}>{phase.label}</Chip>}
                    {!showMap && (
                      <Chip className={statusTone(selectedSession?.session_status).chip}>
                        {prettyStatus(selectedSession?.session_status)}
                      </Chip>
                    )}
                  </div>
                  <div className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-slate-400">
                    Session #{selectedSessionId}
                    {resolvedTracking ? ` · Track #${resolvedTracking}` : ' · no tracking session'}
                    {sessionEnded && detail?.ended_at ? ` · ended ${fmtTime(detail.ended_at)}` : ''}
                  </div>
                </div>

                <span className="hidden sm:block h-8 w-px bg-slate-200"/>

                <div className="flex items-center gap-3">
                  <PersonPill
                    role="Trainer" name={trainerName} icon={User} tone="teal"
                    onClick={detail?.trainer_id ? () => navigate(`/dashboard/user/${detail.trainer_id}`) : undefined}
                  />
                  <PersonPill
                    role="Student" name={studentName} icon={GraduationCap} tone="brand"
                    onClick={detail?.student_id ? () => navigate(`/dashboard/user/${detail.student_id}`) : undefined}
                  />
                </div>
              </div>

              {/* Live speed dial — only while the trainer is actually driving */}
              {showLive && (
                <div className="pointer-events-auto ms-auto rounded-2xl bg-slate-900/90 px-4 py-2.5 text-center shadow-lg ring-1 ring-white/10 backdrop-blur-xl">
                  <div className="text-[9px] font-bold uppercase tracking-[0.1em] text-slate-400">Speed</div>
                  <div className="text-2xl font-black leading-none tabular-nums text-white">{currentSpeed}</div>
                  <div className="text-[9px] font-bold text-slate-400">km/h</div>
                </div>
              )}
            </div>
          )}

          {/* ── Map controls ── */}
          {showMap && (
            <div className="absolute end-3 top-1/2 -translate-y-1/2 flex flex-col gap-2">
              <MapBtn icon={Crosshair} label="Fit route" onClick={recenter}/>
              <MapBtn icon={Navigation} label={follow ? 'Following trainer' : 'Follow trainer'}
                onClick={() => { setFollow(v => !v); if (!follow && livePos) mapRef.current?.panTo(livePos) }}
                active={follow} disabled={!showLive}/>
              <MapBtn icon={Satellite} label={satellite ? 'Map view' : 'Satellite view'}
                onClick={() => setSatellite(v => !v)} active={satellite}/>
              <MapBtn icon={ExternalLink} label="Open booking"
                onClick={() => selectedBookingId && navigate(`/dashboard/booking/${selectedBookingId}`)}
                disabled={!selectedBookingId}/>
            </div>
          )}

          {/* ── Metrics rail ── */}
          {showMap && (
            <div className="absolute inset-x-3 bottom-3 flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-0 overflow-x-auto rounded-2xl bg-white/85 shadow-lg shadow-slate-900/5 ring-1 ring-black/5 backdrop-blur-xl">
                <div className="flex items-stretch divide-white/60">
                  <Metric icon={RouteIcon}  label="Distance"   tone="blue"  value={`${stats.distanceKm.toFixed(2)} km`}/>
                  <Metric icon={Gauge}      label="Avg"        value={`${Math.round(stats.avgSpeed)} km/h`}/>
                  <Metric icon={Gauge}      label="Max"        tone="amber" value={`${Math.round(stats.maxSpeed)} km/h`}/>
                  <Metric icon={TimerReset} label="Duration"
                    value={fmtDuration(detail?.duration_sec || (detail?.started_at ? (Date.now() - new Date(detail.started_at).getTime()) / 1000 : 0))}/>
                  <Metric icon={Clock}      label="Started"    value={fmtTime(detail?.started_at ?? liveRow?.started_at)}/>
                  <Metric icon={MapPin}     label="Points"     tone={gpsStale ? 'amber' : 'slate'}
                    value={stats.count ? (showLive ? `${stats.count} · ${timeAgo(last?.recorded_at)} ago` : String(stats.count)) : '0'}/>
                </div>
              </div>

              {/* Legend */}
              <div className="flex items-center gap-3 rounded-2xl bg-white/85 px-3 py-2 text-[10px] font-bold text-slate-500 shadow-lg shadow-slate-900/5 ring-1 ring-black/5 backdrop-blur-xl">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500"/>Start</span>
                {showLive
                  ? <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-600"/>Trainer</span>
                  : <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500"/>End</span>}
                <span className="flex items-center gap-1.5">
                  <span className={cn('w-4 h-0.5 rounded', showLive ? 'bg-blue-600' : 'bg-slate-500')}/>
                  {showLive ? 'Live route' : 'Recorded'}
                </span>
              </div>
            </div>
          )}

          {/* ── Transient status toasts ── */}
          {showMap && (
            <div className="pointer-events-none absolute end-3 bottom-20 flex flex-col items-end gap-2">
              {routeQ.isLoading && (
                <span className="flex items-center gap-2 rounded-xl bg-white/90 px-3 py-1.5 text-[11px] font-bold text-slate-500 shadow ring-1 ring-black/5 backdrop-blur">
                  <Spinner className="w-3 h-3"/> Loading route…
                </span>
              )}
              {!routeQ.isLoading && points.length === 0 && (
                <span className="rounded-xl bg-white/90 px-3 py-1.5 text-[11px] font-bold text-slate-500 shadow ring-1 ring-black/5 backdrop-blur">
                  {showLive ? 'Waiting for the first GPS point…' : 'No GPS points recorded'}
                </span>
              )}
              {gpsStale && (
                <span className="flex items-center gap-1.5 rounded-xl bg-amber-50/95 px-3 py-1.5 text-[11px] font-bold text-amber-700 shadow ring-1 ring-amber-200 backdrop-blur">
                  <AlertTriangle size={12}/> No GPS update for {timeAgo(last?.recorded_at)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/** Full-stage empty / informational state shown in place of the map. */
function StageMessage({ icon, title, body, footer, tone = 'slate' }: {
  icon: React.ReactNode; title: string; body: string; footer?: React.ReactNode; tone?: 'slate' | 'amber'
}) {
  return (
    <div className={cn('absolute inset-0 flex flex-col items-center justify-center px-8 text-center',
      tone === 'amber' ? 'bg-amber-50/50' : 'bg-slate-50')}>
      {/* faint grid so an empty stage still reads as a map surface */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: 'linear-gradient(to right, #e2e8f0 1px, transparent 1px), linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
        aria-hidden
      />
      <div className="relative flex flex-col items-center gap-2">
        <span className={cn('w-14 h-14 rounded-2xl flex items-center justify-center ring-1 shadow-sm',
          tone === 'amber' ? 'bg-white ring-amber-200' : 'bg-white ring-slate-200')}>
          {icon}
        </span>
        <p className="text-sm font-black tracking-tight text-slate-800">{title}</p>
        <p className="max-w-sm text-xs font-medium leading-relaxed text-slate-500">{body}</p>
        {footer && <div className="mt-1">{footer}</div>}
      </div>
    </div>
  )
}

export default BookingTrackingPage
